import { fetchPage, type ListParams, type PageResponse } from './collections';

/**
 * The trail of `Persistence/Audit` (`13 §4.12`, `13 §6.6`): `GET /audit-logs` for the console at
 * `/admin/audit` and `GET /tickets/{id}/audit-logs` for the ticket's own history, which the
 * ticket route answers because only it knows whether the caller may see that ticket. Both are
 * read-only cursor collections - there is no write surface here, and there never will be.
 */

/**
 * The fifty-eight values of `ck_audit_logs_entity_type` (`AuditEntityTypes`), in its order, `role`
 * included, the two rule catalogues of `AM-133` point 12 among them (migration 0021), the data export
 * of `AM-226` (migration 0049), the tenant's own configuration (migration 0063), the holidays
 * (migration 0068) the two catalogues of the inventory
 * (migration 0070), the deliveries of notices (migration 0071), the charges (migration 0095), the
 * billing rules (migration 0103, `AM-423`), the screens of the configuration (migration 0124), the
 * reports (migration 0125) and the catalogues of the configuration (migration 0181,
 * `AM-897` point 5). It fills the filter of
 * `/admin/audit` and validates the one the URL carries; a row of a type this list does not know is
 * still drawn, with its key in place of a label, because a trail may not hide an entry
 * (`AM-144` point 5).
 */
export const AUDIT_ENTITY_TYPES = [
  'request',
  'ticket',
  'task',
  'time_entry',
  'asset',
  'configuration_item',
  'knowledge_article',
  'invoice',
  'contract',
  'rate_card',
  'billing_period',
  'organization',
  'user',
  'comment',
  'role',
  'approval_rule',
  'automation_rule',
  'data_export',
  'tenant_settings',
  'holiday',
  'asset_type',
  'ci_type',
  'notification_delivery',
  'billable_entry',
  'billing_rule',
  // `AM-534`: the screens of the configuration, each with its own entity.
  'branding',
  'general_options',
  'fiscal_issuer',
  'invoice_numbering',
  // `AM-535`: a report, whose exports are recorded.
  'report',
  // `AM-897` point 5: the catalogues of the configuration, each with its own entity.
  'priority',
  'impact',
  'urgency',
  'priority_matrix',
  'category',
  'status',
  'status_reason',
  'status_transition',
  'task_type',
  'work_type',
  'tax_rate',
  'service',
  'service_offering',
  'service_area',
  'business_hours',
  'department',
  'location',
  'vendor',
  'team',
  'agent_profile',
  'person',
  'technician_role',
  'sla_policy',
  'sla_target',
  'sla_pause_reason',
  'notification_rule',
  'notification_template',
  'maintenance_plan',
] as const;

export type AuditEntityType = (typeof AUDIT_ENTITY_TYPES)[number];

/** The seventeen values of `ck_audit_logs_action` (`AuditActions`); there is no `read`. */
export const AUDIT_ACTIONS = [
  'create',
  'update',
  'delete',
  'state_change',
  'assign',
  'approve',
  'reject',
  'issue',
  'cancel',
  'discard',
  'login',
  'logout',
  'permission_change',
  'export',
  'support_access',
  'payment',
  'email_delivery',
] as const;

export type AuditAction = (typeof AUDIT_ACTIONS)[number];

/** The five values of `ck_audit_logs_actor_type` (`IAuditContext.ActorType`). */
export const AUDIT_ACTOR_TYPES = [
  'user',
  'api_token',
  'system_job',
  'automation_rule',
  'platform_admin',
] as const;

export type AuditActorType = (typeof AUDIT_ACTOR_TYPES)[number];

/**
 * One field of `changes`: the column's own name and the two values as text, `***` included as-is.
 * `oldLabel` and `newLabel` are the names a value stands for (a status, a team, an agent, a
 * contract, an article, a state code in words...) whenever the module that owns it can say so,
 * beside the value and never in its place.
 */
export interface AuditChange {
  field: string;
  oldValue: string | null;
  newValue: string | null;
  oldLabel?: string | null;
  newLabel?: string | null;
}

/** One row of `audit_logs`, exactly as `AuditLogEntry` answers it. */
export interface AuditLogEntry {
  id: string;
  entityType: string;
  entityId: string;
  action: string;
  actorType: string;
  actorUserId: string | null;
  actorDisplayName: string | null;
  actorApiTokenId: string | null;
  platformAdminId: string | null;
  viaSupportAccess: boolean;
  changes: AuditChange[];
  summary: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  correlationId: string | null;
  createdAt: string;
  /** The zone of the tenant (IANA id) in which `createdAt` and the dates inside `changes` are read. */
  timeZone: string;
  /**
   * `AM-536`: the currency the amounts of `changes` are in, as the row recorded it or as its entity has it; absent or
   * null on a row with no amount.
   */
  currency?: string | null;
}

/** The page size `forEntity` asks for: an inline panel, never the whole trail at once. */
const ENTITY_TRAIL_LIMIT = 20;

/** The most identifiers one `entityId[in]` may carry (`AuditLogReader.MaxEntityIds`); the API refuses more. */
export const MAX_ENTITY_IDS = 100;

/**
 * One request's worth of the entities of a trail (`AM-353`): one entity, or at most
 * {@link MAX_ENTITY_IDS} of them, with where its own trail stands.
 */
export interface EntityTrailBatch {
  entityId: string | readonly string[];
  /** The cursor of its next page; `undefined` before the first one is read. */
  cursor: string | undefined;
  /** Whether the API has given every row of the batch. */
  done: boolean;
  /** Rows read and not shown yet, newest first. */
  pending: readonly AuditLogEntry[];
}

/** Where the trail of `AuditTrailPanel` stands between one "Cargar más" and the next. */
export interface EntityTrailCursor {
  batches: readonly EntityTrailBatch[];
}

/** One page of the trail of `AuditTrailPanel`, and where the next one starts (`null` at the end). */
export interface EntityTrailPage {
  items: AuditLogEntry[];
  next: EntityTrailCursor | null;
}

/** The order of the trail (`AuditLogReader`): newest first, and by id between two rows of the same instant. */
function newerFirst(a: AuditLogEntry, b: AuditLogEntry): number {
  const byDate = Date.parse(b.createdAt) - Date.parse(a.createdAt);
  if (byDate !== 0) {
    return byDate;
  }
  return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
}

/** The batches a list of entities is read in: one for an entity or a list the API takes whole, several past it. */
function batchesOf(entityId: string | readonly string[]): EntityTrailBatch[] {
  const fresh = (ids: string | readonly string[]): EntityTrailBatch => ({
    entityId: ids,
    cursor: undefined,
    done: false,
    pending: [],
  });
  if (typeof entityId === 'string' || entityId.length <= MAX_ENTITY_IDS) {
    return [fresh(entityId)];
  }
  const batches: EntityTrailBatch[] = [];
  for (let start = 0; start < entityId.length; start += MAX_ENTITY_IDS) {
    batches.push(fresh(entityId.slice(start, start + MAX_ENTITY_IDS)));
  }
  return batches;
}

export const auditApi = {
  /** `GET /audit-logs` (`audit.view`), for the `/admin/audit` console. */
  list: (params: ListParams, signal?: AbortSignal): Promise<PageResponse<AuditLogEntry>> =>
    fetchPage<AuditLogEntry>('/api/v1/audit-logs', params, signal),

  /**
   * The trail of one entity, for `AuditTrailPanel`: `ticket` goes through its own route (`07
   * §4.7`, `07 §3.2` - the ticket's visibility rule decides whether the row is even readable),
   * which also answers the rows of the ticket's tasks, mixed by date (`AM-317`); everything else through `/audit-logs` filtered by `entityType`/`entityId`. A list of
   * identifiers reads the trail of all of them at once (`entityId[in]`, at most
   * `MAX_ENTITY_IDS`): the panel of an asset shows the changes of its configuration items that way.
   */
  forEntity: (
    entityType: string,
    entityId: string | readonly string[],
    cursor?: string,
    signal?: AbortSignal,
  ): Promise<PageResponse<AuditLogEntry>> => {
    const params: ListParams = {
      limit: ENTITY_TRAIL_LIMIT,
      ...(cursor === undefined ? {} : { cursor }),
    };
    if (typeof entityId !== 'string') {
      return fetchPage<AuditLogEntry>(
        '/api/v1/audit-logs',
        { ...params, filters: { entityType, 'entityId[in]': entityId.join(',') } },
        signal,
      );
    }
    return entityType === 'ticket'
      ? fetchPage<AuditLogEntry>(`/api/v1/tickets/${entityId}/audit-logs`, params, signal)
      : fetchPage<AuditLogEntry>(
          '/api/v1/audit-logs',
          { ...params, filters: { entityType, entityId } },
          signal,
        );
  },

  /**
   * `GET /invoices/{id}/history` (`invoices.view`, `AM-429`): the rows of one document in the trail, newest
   * first, for its "Historia" block, which whoever sees the documents reads without `audit.view`.
   */
  invoiceHistory: (
    invoiceId: string,
    cursor?: string,
    signal?: AbortSignal,
  ): Promise<PageResponse<AuditLogEntry>> =>
    fetchPage<AuditLogEntry>(
      `/api/v1/invoices/${invoiceId}/history`,
      { limit: ENTITY_TRAIL_LIMIT, ...(cursor === undefined ? {} : { cursor }) },
      signal,
    ),

  /**
   * `GET /time-entries/{id}/history` (`AM-461`): the rows of one hour in the trail, newest first, for its
   * "Historia" block, which whoever sees the hour reads without `audit.view`, under the reach of the hours.
   */
  timeEntryHistory: (
    timeEntryId: string,
    cursor?: string,
    signal?: AbortSignal,
  ): Promise<PageResponse<AuditLogEntry>> =>
    fetchPage<AuditLogEntry>(
      `/api/v1/time-entries/${timeEntryId}/history`,
      { limit: ENTITY_TRAIL_LIMIT, ...(cursor === undefined ? {} : { cursor }) },
      signal,
    ),

  /**
   * `GET /billing-periods/{id}/history` (`billing_periods.view`, `AM-455`): the rows of one period in the
   * trail, newest first, for the "Historia" block of its ficha, without `audit.view`.
   */
  periodHistory: (
    periodId: string,
    cursor?: string,
    signal?: AbortSignal,
  ): Promise<PageResponse<AuditLogEntry>> =>
    fetchPage<AuditLogEntry>(
      `/api/v1/billing-periods/${periodId}/history`,
      { limit: ENTITY_TRAIL_LIMIT, ...(cursor === undefined ? {} : { cursor }) },
      signal,
    ),

  /** `GET /billable-entries/{id}/history` (`billing.view`, `AM-455`): the rows of one charge in the trail. */
  chargeHistory: (
    entryId: string,
    cursor?: string,
    signal?: AbortSignal,
  ): Promise<PageResponse<AuditLogEntry>> =>
    fetchPage<AuditLogEntry>(
      `/api/v1/billable-entries/${entryId}/history`,
      { limit: ENTITY_TRAIL_LIMIT, ...(cursor === undefined ? {} : { cursor }) },
      signal,
    ),

  /**
   * One page of the trail `AuditTrailPanel` shows (`AM-353`): {@link forEntity} for one entity or for a
   * list the API takes in one request, and for a longer list (the configuration items of an asset,
   * past {@link MAX_ENTITY_IDS}) one request per batch of at most that many, merged newest first. Each
   * batch pages on its own; a page shows rows only while every batch that may still hold a newer one
   * has some read, so the merged trail keeps the order of a single one across "Cargar más".
   */
  trailPage: async (
    entityType: string,
    entityId: string | readonly string[],
    from: EntityTrailCursor | null,
    signal?: AbortSignal,
  ): Promise<EntityTrailPage> => {
    const batches = (from?.batches ?? batchesOf(entityId)).map((batch) => ({
      ...batch,
      pending: [...batch.pending],
    }));

    await Promise.all(
      batches.map(async (batch) => {
        while (batch.pending.length === 0 && !batch.done) {
          const page = await auditApi.forEntity(entityType, batch.entityId, batch.cursor, signal);
          batch.pending = [...page.items];
          batch.cursor = page.page.nextCursor ?? undefined;
          batch.done = batch.cursor === undefined;
        }
      }),
    );

    const items: AuditLogEntry[] = [];
    while (items.length < ENTITY_TRAIL_LIMIT) {
      let newest: (typeof batches)[number] | undefined;
      for (const batch of batches) {
        const head = batch.pending[0];
        if (
          head !== undefined &&
          (newest === undefined || newerFirst(head, newest.pending[0]!) < 0)
        ) {
          newest = batch;
        }
      }
      if (newest === undefined) {
        break;
      }
      items.push(newest.pending.shift()!);
      // Its next page may hold a row newer than what the other batches have read: it is read first.
      if (newest.pending.length === 0 && !newest.done) {
        break;
      }
    }

    const more = batches.some((batch) => batch.pending.length > 0 || !batch.done);
    return { items, next: more ? { batches } : null };
  },
};
